import { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { getApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import {
  ZEBRA_BROWSER_PRINT_UNAVAILABLE_MESSAGE,
  type ZebraPrinterConnectionStatus,
  type ZebraPrinterDevice,
} from "./types";
import { isBrowserPrintAvailable, sendPrintData, setupPrinter } from "./zebraPrinterService";

export interface ZebraPrinterContextValue {
  status: ZebraPrinterConnectionStatus;
  error: string | null;
  device: ZebraPrinterDevice | null;
  retry: () => Promise<void>;
  printData: (zpl: string | string[]) => Promise<string>;
}

export const ZebraPrinterContext = createContext<ZebraPrinterContextValue | undefined>(undefined);

const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";

export function ZebraPrinterProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<ZebraPrinterConnectionStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [device, setDevice] = useState<ZebraPrinterDevice | null>(null);

  const connect = useCallback(async () => {
    if (typeof window === "undefined") {
      return;
    }
    if (!window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
      setStatus("idle");
      setError(null);
      setDevice(null);
      return;
    }

    if (!isBrowserPrintAvailable()) {
      setDevice(null);
      setStatus("error");
      setError(ZEBRA_BROWSER_PRINT_UNAVAILABLE_MESSAGE);
      return;
    }

    setStatus("connecting");
    setError(null);
    try {
      const d = await setupPrinter();
      setDevice(d);
      setStatus("connected");
    } catch (e) {
      setDevice(null);
      setStatus("error");
      setError(getApiErrorMessage(e) || String(e));
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mq = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const onViewportChange = () => {
      void connect();
    };

    void connect();
    mq.addEventListener("change", onViewportChange);
    return () => mq.removeEventListener("change", onViewportChange);
  }, [connect]);

  const printData = useCallback(
    async (zpl: string | string[]) => {
      // TEMP DEBUG OVERRIDE: skip the printer-connected check and log the raw ZPL
      // so it can be inspected without a physical printer attached. Revert once confirmed.
      console.log("ZPL for print:", zpl);
      if (!device) {
        throw new Error("No printer connected.");
      }
      return sendPrintData(device, zpl);
    },
    [device]
  );

  const value = useMemo(
    () => ({
      status,
      error,
      device,
      retry: connect,
      printData,
    }),
    [status, error, device, connect, printData]
  );

  return <ZebraPrinterContext.Provider value={value}>{children}</ZebraPrinterContext.Provider>;
}
